import React from 'react'

import { HeadProps, PageProps, graphql } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'

import Image from '@components/Content/Image/Image'
import NumberCountersSection from '@components/Content/NumberCounters/NumberCountersSection'
import RequestACallback from '@components/Content/RequestACallback/RequestACallback'
import HeadTags from '@components/Content/SEO/HeadTags'
import Showcase from '@components/Content/Showcase/Showcase'
import ExternalArticlesSection from '@components/ExternalArticles/ExternalArticlesSection'
import FAQSection from '@components/FAQs/FAQSection'
import Layout from '@components/Layout/Layout'
import FeaturedPostsSection from '@components/News/FeaturedPostsSection'
import HomepageHero from '@components/Pages/Homepage/HomepageHero'
import HomepageSolutions from '@components/Pages/Homepage/HomepageSolutions'
import PartnersSection from '@components/Partners/PartnersSection'
import ServicesSection from '@components/Services/ServicesSection'

import { HomepageDataType } from '@contracts/Homepage'

import * as styles from './homepage.module.scss'

const index = (props: PageProps<HomepageDataType>) => {
	const { pathname } = props.location
	const { hero, numberCounters, servicesSection, industriesShowcase, solutionsSection, partnersSection, faqs } =
		props.data.wpPage.homepageOptions

	const heroBackground = (
		<StaticImage src={'../images/homepage-hero-poster.jpg'} alt="" objectFit="cover" height={600} />
	)

	const servicesRef = React.useRef<HTMLDivElement>(null)

	return (
		<Layout id={props.data.wpPage.databaseId} path={pathname}>
			<HomepageHero
				data={hero}
				background={heroBackground}
				scrollTo={servicesRef}
				classes={{ titles: styles.titles, title: styles.title }}
			/>

			<NumberCountersSection data={numberCounters} />

			<ServicesSection data={servicesSection} ref={servicesRef} />

			<Showcase data={industriesShowcase}>
				<Image image={industriesShowcase.image} altText="" />
			</Showcase>

			<HomepageSolutions
				mainShowcase={solutionsSection.mainShowcase}
				gridShowcase={solutionsSection.gridShowcase}
				image={solutionsSection.mainShowcase.image}
			/>

			<ExternalArticlesSection />

			<FeaturedPostsSection />

			<PartnersSection data={partnersSection} style="secondary" />

			<FAQSection data={faqs} />

			<RequestACallback />
		</Layout>
	)
}

export default index

export function Head(props: HeadProps<HomepageDataType>) {
	return <HeadTags seo={props.data.wpPage.seo} />
}

export const query = graphql`
	query Homepage {
		wpPage(isFrontPage: { eq: true }) {
			databaseId
			homepageOptions {
				hero {
					styledTitle {
						styledText {
							text
							isYellow
							isThin
							isSplit
						}
					}
					subtitle
					button {
						buttonType
						label
						internalLink {
							... on WpPage {
								link
							}
							... on WpPost {
								link
							}
							... on WpService {
								link
							}
						}
						externalLink
					}
				}
				numberCounters {
					counters {
						number
						numberSuffix
						text
					}
				}
				servicesSection {
					styledTitle {
						styledText {
							text
							isYellow
							isThin
							isSplit
						}
					}
					subtext
					services {
						service {
							... on WpService {
								title
								uri
								serviceOptions {
									icon {
										mimeType
										localFile {
											publicURL
											childImageSharp {
												gatsbyImageData(width: 150)
											}
										}
									}
								}
							}
						}
					}
				}
				industriesShowcase {
					styledTitle {
						styledText {
							text
							isYellow
							isThin
							isSplit
						}
					}
					subtitle
					content
					footnote
					button {
						buttonType
						label
						externalLink
						internalLink {
							... on WpPage {
								link
							}
							... on WpPost {
								link
							}
							... on WpService {
								link
							}
						}
					}
					image {
						mimeType
						localFile {
							publicURL
							childImageSharp {
								gatsbyImageData(width: 600)
							}
						}
					}
				}
				solutionsSection {
					mainShowcase {
						styledTitle {
							styledText {
								text
								isYellow
								isThin
								isSplit
							}
						}
						subtitle
						content
						footnote
						button {
							buttonType
							label
							externalLink
							internalLink {
								... on WpPage {
									link
								}
								... on WpPost {
									link
								}
								... on WpService {
									link
								}
							}
						}
						image {
							mimeType
							localFile {
								publicURL
								childImageSharp {
									gatsbyImageData(width: 800)
								}
							}
						}
					}
					gridShowcase {
						styledTitle {
							styledText {
								text
								isYellow
								isThin
								isSplit
							}
						}
						subtitle
						content
						footnote
						button {
							buttonType
							label
							externalLink
							internalLink {
								... on WpPage {
									link
								}
								... on WpPost {
									link
								}
								... on WpService {
									link
								}
							}
						}
						cards {
							card {
								text
								link {
									... on WpPage {
										link
									}
									... on WpPost {
										link
									}
									... on WpService {
										link
									}
								}
								icon {
									mimeType
									localFile {
										publicURL
										childImageSharp {
											gatsbyImageData(width: 400)
										}
									}
								}
								hoverContent
							}
						}
					}
				}
				partnersSection {
					styledTitle {
						styledText {
							text
							isYellow
							isThin
							isSplit
						}
					}
					partners {
						partner {
							... on WpPartner {
								id
								title
								featuredImage {
									node {
										mimeType
										localFile {
											publicURL
											childImageSharp {
												gatsbyImageData(width: 450)
											}
										}
									}
								}
							}
						}
					}
				}

				faqs {
					faq {
						... on WpFaq {
							title
							content
						}
					}
				}
			}
			seo {
				metaDesc
				metaKeywords
				metaRobotsNoindex
				metaRobotsNofollow
				opengraphAuthor
				opengraphDescription
				opengraphModifiedTime
				opengraphPublishedTime
				opengraphPublisher
				opengraphSiteName
				opengraphTitle
				opengraphType
				opengraphUrl
				opengraphImage {
					localFile {
						publicURL
					}
				}
				readingTime
				title
				twitterTitle
				schema {
					articleType
					pageType
					raw
				}
				twitterDescription
				twitterImage {
					localFile {
						publicURL
					}
				}
			}
		}
	}
`
